/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from './config.js';
import { contentPreprocessors } from './content-preprocessors.js';

/*
 * Overlays that fetch their own content.
 *
 * `openDialog` loads a URL into a panel; `videoLightbox` turns a link into a
 * player over the page. Both are dialogs, so both hand focus management to
 * the shared accessibility helpers rather than doing it themselves.
 */

/**
 * Fetches HTML from `url` and displays it in a floating dialog appended to `container`.
 * The dialog is positioned and sized as a fraction of the container's dimensions.
 * @param {string} url - URL to fetch dialog HTML from.
 * @param {Element} container - Parent element that the dialog is appended to.
 * @param {object} [options]
 * @param {number} [options.x=0] - Left offset as a fraction of container width.
 * @param {number} [options.y=0] - Top offset as a fraction of container height.
 * @param {number} [options.width=1] - Width as a fraction of container width.
 * @param {number} [options.height=1] - Height as a fraction of container height.
 * @param {string} [options.closeSelector] - CSS selector for the close button inside the loaded HTML.
 * @returns {void}
 */
export const openDialog = (url, container, options = {}) => {
    const { x = 0, y = 0, width = 1, height = 1, closeSelector } = options;

    // normalize container
    const containerEl = container;

    // create dialog with DOM APIs
    const dialog = document.createElement('div');
    dialog.className = 'dialog';
    Object.assign(dialog.style, { position: 'absolute', zIndex: 1 });
    containerEl.appendChild(dialog);

    const layout = () => {
        const containerWidth = containerEl.clientWidth;
        const containerHeight = containerEl.clientHeight;
        const padding = 0.1 * Math.min(containerWidth, containerHeight);
        const padding2 = 2 * padding;

        Object.assign(dialog.style, {
            left: `${containerWidth * x}px`,
            top: `${containerHeight * y}px`,
            width: `${containerWidth * width - padding2}px`,
            height: `${containerHeight * height - padding2}px`,
            padding: `${padding}px`
        });

        const sections = dialog.querySelectorAll('section');
        const section = dialog.querySelector('section.active');
        if (!section) return;
        const paddingY = section.offsetHeight - section.clientHeight;
        sections.forEach((s) => {
            s.style.height = `${containerHeight * height - padding2 - paddingY}px`;
        });
    };

    const open = () => {
        dialog.classList.add('is-open');
    };
    const close = () => {
        dialog.classList.remove('is-open');
        // Remove dialog after CSS transition
        const onEnd = (e) => {
            if (e.propertyName === 'opacity') {
                dialog.removeEventListener('transitionend', onEnd);
                dialog.remove();
            }
        };
        dialog.addEventListener('transitionend', onEnd);
    };

    window.addEventListener('resize', layout);
    // Start hidden (CSS handles initial opacity 0)
    // Ensure that the dialog is closed until content loads
    dialog.classList.remove('is-open');

    fetch(url, { credentials: 'same-origin' })
        .then((r) => r.text())
        .then((html) => {
            const safeHtml = contentPreprocessors?.applyToHTML
                ? contentPreprocessors.applyToHTML(html)
                : html;
            dialog.innerHTML = safeHtml;

            const sections = dialog.querySelectorAll('section');
            let section = sections[0];
            if (section) section.classList.add('active');

            let closeElem;
            if (closeSelector) {
                closeElem = dialog.querySelector(closeSelector);
            } else {
                const a = document.createElement('a');
                a.href = '#';
                a.className = 'close';
                const img = document.createElement('img');
                img.src = '/static/help/close.svg';
                a.appendChild(img);
                dialog.appendChild(a);
                closeElem = a;
            }

            if (closeElem) {
                const stop = (ev) => {
                    ev.preventDefault();
                    close();
                };
                closeElem.addEventListener('click', stop);
                closeElem.addEventListener('touchstart', stop, {
                    passive: false
                });
            }

            layout();
            open();

            // hide non-first sections
            sections.forEach((s, i) => {
                if (i > 0) s.style.display = 'none';
            });

            dialog.querySelectorAll('section a.next').forEach((a) => {
                const onNext = (event) => {
                    event.preventDefault();
                    const next =
                        section?.nextElementSibling &&
                        section.nextElementSibling.tagName === 'SECTION'
                            ? section.nextElementSibling
                            : null;
                    if (next) {
                        section.classList.remove('active');
                        section.style.display = 'none';
                        next.classList.add('active');
                        next.style.display = '';
                        section = next;
                    } else {
                        close();
                    }
                };
                a.addEventListener('click', onNext);
                a.addEventListener('touchstart', onNext, { passive: false });
            });
        })
        .catch((err) => {
            // if loading fails, just remove the dialog
            console.error(
                'openDialog() failed to load content:',
                err
            );
            dialog.remove();
        });
};

/**
 * Attaches a video lightbox to `button`. On click the video referenced by the
 * button's `href` attribute is shown fullscreen. Supports local video files
 * (mp4/webm `<video>`) and external embed URLs (`<iframe>`).
 * @param {Element} container - Element that the lightbox overlay is appended to.
 * @param {Element} button - Trigger element whose `href` is the video URL.
 * @returns {void}
 */
export const videoLightbox = (container, button) => {
    const containerEl = container;
    const btn = button;

    const onOpen = (event) => {
        event.preventDefault();

        const lightbox = document.createElement('div');
        lightbox.className = 'video-lightbox';
        containerEl.appendChild(lightbox);

        const lightboxContent = document.createElement('div');
        lightboxContent.className = 'video-lightbox-content';
        lightbox.appendChild(lightboxContent);

        const videoUrl = btn.getAttribute('href');
        const isExternal = /^https?:\/\//.test(videoUrl);

        let video;
        if (!isExternal) {
            const videoBaseUrl = videoUrl.replace(/\.(mp4|webm)$/, '');
            const v = document.createElement('video');
            v.setAttribute('autoplay', 'autoplay');
            v.setAttribute('controls', 'controls');
            v.setAttribute('loading', 'lazy');
            v.setAttribute('poster', `${videoBaseUrl}.jpg`);
            v.setAttribute('preload', 'none');
            v.style.width = '960px';
            v.style.height = 'auto';
            v.style.maxWidth = '95%';

            const sWebm = document.createElement('source');
            sWebm.src = `${videoBaseUrl}.webm`;
            sWebm.type = 'video/webm; codecs="vp8.0, vorbis"';
            v.appendChild(sWebm);

            const sMp4 = document.createElement('source');
            sMp4.src = `${videoBaseUrl}.mp4`;
            sMp4.type = 'video/mp4; codecs="avc1.4D401F, mp4a.40.2"';
            v.appendChild(sMp4);

            video = v;
        } else {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('loading', 'lazy');
            iframe.setAttribute(
                'title',
                getConfig()?.uiText?.a11y?.videoPlayer || 'Video'
            );
            iframe.src = `${videoUrl}?autoplay=1`;
            // data-attrs may be JSON or plain attributes serialized
            const raw = btn.dataset?.attrs;
            if (raw) {
                try {
                    const attrs = JSON.parse(raw);
                    for (const attrName in attrs) {
                        iframe.setAttribute(attrName, attrs[attrName]);
                    }
                } catch (_) {
                    // ignore malformed data-attrs
                }
            }
            video = iframe;
        }

        lightboxContent.appendChild(video);

        const close = () => {
            lightbox.classList.remove('is-open');
            const onEnd = (e) => {
                if (e.propertyName === 'opacity') {
                    lightbox.removeEventListener('transitionend', onEnd);
                    if (!isExternal && video && video.pause) {
                        try {
                            video.pause();
                        } catch {
                            // A cross-origin embed will not let us pause
                            // it. Closing the lightbox removes it anyway.
                        }
                    }
                    lightbox.remove();
                }
            };
            lightbox.addEventListener('transitionend', onEnd);
        };

        const closeButton = document.createElement('a');
        closeButton.href = '#';
        closeButton.className = 'close';
        const img = document.createElement('img');
        img.src = '/static/help/close.svg';
        img.alt = '';
        closeButton.appendChild(img);
        closeButton.setAttribute(
            'aria-label',
            getConfig()?.uiText?.a11y?.closeDialog || 'Close'
        );
        const stop = (ev) => {
            ev.preventDefault();
            close();
        };
        closeButton.addEventListener('click', stop);
        closeButton.addEventListener('touchstart', stop, { passive: false });

        const controls = document.createElement('div');
        Object.assign(controls.style, { position: 'fixed', right: 0, top: 0 });
        controls.appendChild(closeButton);
        lightbox.insertBefore(controls, lightbox.firstChild);

        // start hidden, then fade in
        requestAnimationFrame(() => {
            lightbox.classList.add('is-open');
        });
    };

    btn.addEventListener('click', onOpen);
    btn.addEventListener('touchstart', onOpen, { passive: false });
};
